import * as uuid from "uuid";
import React, { useEffect, useState } from "react";
import { DocumentPartTree, DocumentPartTreeSelection, TreeViewDataItem } from "../cpm/DocumentPartTree";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { openDocumentById } from "../../utils/helpers/OpenDocumentHelper";
import { ActionButton, IButtonStyles, IContextualMenuProps, IIconProps, ImageFit } from "@fluentui/react";
import {
  fetchAllDocumentPartStatus,
  fetchAllUserDocumentCheckout,
  fetchDocumentPartStatus,
  fetchDriveItemName,
} from "../../api/document/DocumentsPart";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  selectCpmUsername,
  selectIsSendingIxbrl,
  selectIxbrlSettings,
  selectSeahorseDocumentLinked,
  selectServiceProvider,
  selectUserFunctions,
  setAllUserDocumentCheckout,
} from "../../redux/feature/settings/configurations-slice";
import { DocumentPosition } from "../../api/models/document-position";
import { fetchInitialDocumentPartTreeProps } from "../cpm/DocumentPartTreeHelper";
import { ServiceProvider } from "../../api/models/service-provider";
import { LinkedDocumentInfo } from "../../api/models/linked-document-info";
import { store } from "../../redux/store/store";
import { DocumentRoot } from "../../api/models/document-root";
import { DocumentPart, ElaborationItem } from "../../api/models";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { IxbrlSetting } from "../../api/models/ixbrl-setting";
import { treeMap } from "../../utils/data-structures/tree/treeMap";
import {
  createElaborationRequestAsync,
  ElaborationStatusItem,
} from "../../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { TgkRenameElaborationRequest } from "../../api/models/tgk-rename-elaboration-request";
import NotificationManager from "../../common/notification/NotificationManager";
import { fetchInDesignLinkedDocument } from "../../api/document/InDesign";
import {
  addAlignDocumentItemMenu,
  addDeleteItemMenu,
  addExportDifferencesItemMenu,
  addExportItemMenu,
  addExportWorkflowItemMenu,
  addRenameDocumentItemMenu,
  addSendCopyToIXBRLItemMenu,
  addSendToInDesignItemMenu,
  isDocumentPartSubmitted,
} from "./DocumentOutlineUtils";
import { SkeletonLoader } from "../../common/loading/SkeletonLoader";
import {
  CAPTION_BASE_TOOLS,
  CAPTION_CHANGE_TEMPLATE_DISABLED_SUBMITTED,
  CAPTION_OUTLINE_CHANGE_COMPONENT_SOURCE,
  CAPTION_OUTLINE_CHANGE_TEMPLATE,
} from "./outlineCaptions";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";

const OutlineSkeleton = () => (
  <div style={{ paddingTop: "0.5rem" }}>
    <SkeletonLoader />
  </div>
);

type ToolsMenuPage = "dbmapping" | "changecomponentsource" | "changetemplate";
const DISABLED_COMPONENT_ICON_FILTER = "grayscale(100%) opacity(50%)";

export interface GetToolsMenuPropsOptions {
  hasReadPermission: boolean;
  notAuthorizedUserForDbmapping: boolean;
  isRootSubmitted?: boolean;
  onClick: (page: ToolsMenuPage) => void;
}

export const getToolsMenuProps = (options: GetToolsMenuPropsOptions): IContextualMenuProps => {
  const { hasReadPermission, notAuthorizedUserForDbmapping, isRootSubmitted, onClick } = options;
  const isMenuItemDisabled = notAuthorizedUserForDbmapping || !hasReadPermission;
  const isChangeTemplateDisabled = isMenuItemDisabled || !!isRootSubmitted;

  return {
    items: [
      {
        key: CAPTION_OUTLINE_CHANGE_TEMPLATE,
        text: formatMessage(CAPTION_OUTLINE_CHANGE_TEMPLATE),
        onClick: () => onClick("changetemplate"),
        iconProps: {
          iconName: "PageList",
        },
        disabled: isChangeTemplateDisabled,
        title:
          isRootSubmitted && !isMenuItemDisabled
            ? formatMessage(CAPTION_CHANGE_TEMPLATE_DISABLED_SUBMITTED)
            : undefined,
        itemProps: {
          "aria-disabled": isChangeTemplateDisabled || undefined,
        },
      },
      {
        key: "cof.opendocuments.outline.dbmapping",
        text: formatMessage("cof.opendocuments.outline.dbmapping"),
        onClick: () => onClick("dbmapping"),
        iconProps: {
          imageProps: {
            src: isMenuItemDisabled ? "/assets/dbMappingDisabled.svg" : "/assets/dbMapping.svg",
            imageFit: ImageFit.contain,
            width: "16px",
            height: "16px",
          },
        },
        disabled: isMenuItemDisabled,
      },
      {
        // IDS-7053 automation: Fluent forwards native props from the top-level contextual-menu item to its
        // outer button. itemProps belongs to the inner renderer and would not put this id on the DOM button.
        id: generateIdentifier("change component source", "button"),
        key: CAPTION_OUTLINE_CHANGE_COMPONENT_SOURCE,
        text: formatMessage(CAPTION_OUTLINE_CHANGE_COMPONENT_SOURCE),
        onClick: () => onClick("changecomponentsource"),
        iconProps: {
          imageProps: {
            src: "/assets/component-icon.svg",
            imageFit: ImageFit.contain,
            width: "16px",
            height: "16px",
            style: isMenuItemDisabled ? { filter: DISABLED_COMPONENT_ICON_FILTER } : undefined,
          },
        },
        disabled: isMenuItemDisabled,
      },
    ],
    directionalHintFixed: true,
  };
};

export interface GetIconButtonMenuPropsOptions {
  itemSelected: TreeViewDataItem;
  itemHierarchicalIndex: string;
  userFunctions: UserFunction[];
  defaultServiceProvider: ServiceProvider;
  seahorseDocumentLinked: LinkedDocumentInfo[];
  inDesignLinkedDocument?: LinkedDocumentInfo;
  allIxbrlSettings: IxbrlSetting[];
  idPartRoot: string;
  rootId: string;
  isSendingIxbrl: boolean;
  onRenameDocument?: (item: FlatPathTreeItem) => void;
  onDeleteDocument?: (item: FlatPathTreeItem) => void;
  onExportDocument?: (item: FlatPathTreeItem) => void;
  documentPartWithStatus?: DocumentPartWithStatus[];
  onCheckDifferencesDocument?: (item: FlatPathTreeItem) => void;
  onAlignDocument?: (item: FlatPathTreeItem) => void;
  isCertifiedOrApprovedOrRejected?: boolean;
}

export const getIconButtonMenuProps = (options: GetIconButtonMenuPropsOptions): IContextualMenuProps => {
  const {
    itemSelected,
    itemHierarchicalIndex,
    userFunctions,
    defaultServiceProvider,
    seahorseDocumentLinked,
    inDesignLinkedDocument,
    allIxbrlSettings,
    idPartRoot,
    rootId,
    isSendingIxbrl,
    onRenameDocument,
    onDeleteDocument,
    onExportDocument,
    documentPartWithStatus,
    onCheckDifferencesDocument,
    onAlignDocument,
    isCertifiedOrApprovedOrRejected,
  } = options;
  let notAuthorizedUserForRename =
    isNotAuthorizedUser(UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_PART, userFunctions) &&
    isNotAuthorizedUser(UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_ROOT, userFunctions);
  let notAuthorizedUserForDelete = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DELETE_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForSendCopyIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);
  let notAuthorizedUserForEditRoot = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_ROOT,
    userFunctions
  );

  const items: any[] = [];

  /*
          Pulsante spostato nella library fin quando non vengono risolti gli errori nella pagina "Outline" in caso di documenti con moltissime docparts.
          Dopo la risoluzione, va rimosso dalla library e riattivato qui.
         */
  // items.push({
  //   id: "download-copy",
  //   key: "download-copy",
  //   text: formatMessage("cof.base.downloadacopy"),
  //   onClick: () => {
  //     store.dispatch(downloadDocumentCopy({ document: itemSelected }));
  //     store.dispatch(navigate(DOWNLOAD_COPY));
  //   },
  //   hidden: itemSelected.key != rootId,
  //   disabled: false,

  // });
  addRenameDocumentItemMenu(
    items,
    itemSelected,
    onRenameDocument,
    idPartRoot,
    notAuthorizedUserForRename,
    documentPartWithStatus
  );
  addAlignDocumentItemMenu(items, itemSelected, onAlignDocument, notAuthorizedUserForEditRoot);
  addExportDifferencesItemMenu(items, itemSelected, onCheckDifferencesDocument);
  addExportWorkflowItemMenu(items, itemSelected, isCertifiedOrApprovedOrRejected);
  addDeleteItemMenu(
    items,
    idPartRoot,
    itemSelected,
    onDeleteDocument,
    notAuthorizedUserForDelete,
    documentPartWithStatus,
    itemHierarchicalIndex
  );
  addExportItemMenu(items, itemSelected, onExportDocument);
  addSendCopyToIXBRLItemMenu(
    items,
    rootId,
    itemSelected,
    defaultServiceProvider,
    notAuthorizedUserForSendCopyIXBRL,
    allIxbrlSettings,
    seahorseDocumentLinked,
    isSendingIxbrl
  );
  addSendToInDesignItemMenu(items, rootId, itemSelected, inDesignLinkedDocument);

  return {
    items,
    directionalHintFixed: true,
    //TODO this props does nothing in IconButtonProps.menuProps, should be removed
    hidden: !(itemSelected.key != idPartRoot && (itemHierarchicalIndex.match(/_/g)?.length ?? 0) < 2),
  };
};

// IDS-5755: exported (moved out of DocumentOutline, it has no closure dependency on component state)
// purely so the merge logic can be unit-tested in isolation. Used by the background status-loading
// effect ([elementToUpdate]) to compute which freshly-fetched statuses are genuinely new for the
// current documentPartWithStatus array.
export function getNewElements(
  documentPartWithStatusReturnedBackup: DocumentPartWithStatus[],
  prevState: DocumentPartWithStatus[]
) {
  const newElements: DocumentPartWithStatus[] = [];
  const documentIds = new Set<string>();
  for (const element of prevState) {
    documentIds.add(element.documentPartId);
  }
  for (const element of documentPartWithStatusReturnedBackup) {
    // IDS-5755: skip null/undefined workflowStatus placeholders. This loader is append-only by id
    // (it never overwrites an existing entry), so storing a null placeholder here would permanently
    // mark the part as "loading": a later response carrying the real status for the same id would be
    // dropped because the id is already considered "present". Leaving the id absent instead lets that
    // later response be appended normally once the real status becomes available.
    if (element.workflowStatus == null) continue;
    if (!documentIds.has(element.documentPartId)) {
      newElements.push(element);
    }
  }
  return newElements;
}

export const DocumentOutline = ({
  isOutlineRibbon,
  isSourcesRibbon,
  isLibraryRibbon,
  isOpenOutlineRibbon,
  selectedTreeItem,
  onDeleteDocument,
  onSetSelectedItems,
  onExportDocument,
  onItemClick,
  idPartRoot,
  isLinkExisting,
  updateTreeItems,
  getIconButtonOutlineRibbon,
  changePage,
  parents,
  ignoreFetchPath,
  documentPartWithStatus,
  setDocumentPartWithStatus,
  onCheckDifferencesDocument,
  onAlignDocument,
  blockingElaboration,
  siteId,
  driveItemId,
  setIsCertifiedOrApprovedOrRejected,
  isCertifiedOrApprovedOrRejected,
  enableFilter,
}: {
  isOutlineRibbon?: boolean;
  isSourcesRibbon?: boolean;
  isLibraryRibbon?: boolean;
  isOpenOutlineRibbon?: boolean;
  selectedTreeItem: FlatPathTreeItem;
  onDeleteDocument?: (item: FlatPathTreeItem) => void;
  onSetSelectedItems?: (item: TreeViewDataItem) => void;
  onExportDocument?: (item: FlatPathTreeItem) => void;
  onItemClick?: (selection: DocumentPartTreeSelection) => void;
  idPartRoot?: string;
  isLinkExisting?: boolean;
  updateTreeItems?: (treeItems: any, modifiedFields: boolean, showButton: boolean) => void;
  getIconButtonOutlineRibbon?: (
    item: any,
    itemHierarchicalIndex: string,
    documentPartWithStatus: DocumentPartWithStatus[]
  ) => IContextualMenuProps;
  changePage?: (page: string) => void;
  parents?: DocumentPosition[];
  ignoreFetchPath?: boolean;
  documentPartWithStatus?: DocumentPartWithStatus[];
  setDocumentPartWithStatus?: (
    documentPartWithStatus: (prevState: DocumentPartWithStatus[]) => DocumentPartWithStatus[]
  ) => void;
  onCheckDifferencesDocument?: (documentPart: FlatPathTreeItem) => void;
  onAlignDocument?: (documentPart: FlatPathTreeItem) => void;
  blockingElaboration?: ElaborationStatusItem;
  siteId?: string;
  driveItemId?: string;
  setIsCertifiedOrApprovedOrRejected?: (isInitialState: boolean) => void;
  isCertifiedOrApprovedOrRejected?: boolean;
  /** IDS-5755: gates the outline status filter (search bar filter button/dropdown). Defaults to falsy (hidden). */
  enableFilter?: boolean;
}) => {
  const [treeItems, setTreeItems] = useState<TreeViewDataItem[]>([
    {
      text: selectedTreeItem.document.name.replace(/\.docx$/, ""),
      key: selectedTreeItem.document.id,
      hasChildren: selectedTreeItem.document.hasChildren,
      nodeType: "part",
      expanded: false,
      items: undefined,
      data: selectedTreeItem.document,
      hasReadPermissions: selectedTreeItem.readPermission,
    },
  ]);
  const [elementToUpdate, setElementToUpdate] = useState<DocumentRoot[]>([]);
  const rootId = selectedTreeItem.document.id;
  const hasReadPermission = selectedTreeItem.readPermission;
  const username = useAppSelector(selectCpmUsername);
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const seahorseDocumentLinked: LinkedDocumentInfo[] = useAppSelector(selectSeahorseDocumentLinked);
  const allIxbrlSettings: IxbrlSetting[] = useAppSelector(selectIxbrlSettings);
  const [inDesignLinkedDocument, setInDesignLinkedDocument] = useState<LinkedDocumentInfo | undefined>(undefined);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const iconManageStructure: IIconProps = { iconName: "Move" };

  useEffect(() => {
    if (!blockingElaboration) void loadData();
  }, [selectedTreeItem, blockingElaboration]);

  useEffect(() => {
    // TODO PERFORMANCE this is done for all the element, and the BE expand all the node starting from the root everytime
    if (elementToUpdate.length) {
      fetchDocumentPartStatus(rootId, getDocumentIds()).then((documentPartWithStatusReturned) => {
        const documentPartWithStatusReturnedBackup = documentPartWithStatusReturned || [];
        setDocumentPartWithStatus?.((prevState: DocumentPartWithStatus[] | null) => {
          const safePrevState = prevState ?? [];
          const newElements = getNewElements(documentPartWithStatusReturnedBackup, safePrevState);
          return [...safePrevState, ...newElements];
        });
      });
    }
  }, [elementToUpdate]);

  const checkSharepointName = async (documentName: string) => {
    let sharepointName: string;
    console.info(`Check Sharepoint name for document ${documentName} with id ${selectedTreeItem.document.id}`);

    if (!hasReadPermission) {
      console.info(`Exit from Check Sharepoint name because user has not read permission for the document`);
      return;
    }

    if (selectedTreeItem.key != idPartRoot) {
      //IF the part we're editing is not the one for which the outline is opened
      sharepointName = await fetchDriveItemName(selectedTreeItem.document.driveItemId);
    } else {
      sharepointName = await new Promise((resolve, reject) => {
        Office.context.document.getFilePropertiesAsync(function (asyncResult) {
          if (asyncResult?.status == Office.AsyncResultStatus.Succeeded) {
            const sharePointFullPath = asyncResult.value.url.split("/");
            resolve(sharePointFullPath[sharePointFullPath.length - 1]);
          }
          reject(new Error("Cannot get SharePoint file name"));
        });
      });
    }

    if (sharepointName.replace(/\.docx$/, "") !== documentName.replace(/\.docx$/, ""))
      NotificationManager.addNotification({
        key: "checkSharepointName",
        title: null,
        message: formatMessageWithParams("cof.outline.edit.documentnamenotmatch.info", {
          documentName,
          sharepointName: sharepointName,
        }),
      });

    console.info(`Checked Sharepoint name for document ${documentName} with id ${selectedTreeItem.document.id}`);
  };

  const loadInDesignLinkedDocument = async () => {
    const linkedDocument = await fetchInDesignLinkedDocument(rootId, { params: { notShowLoading: true } });
    setInDesignLinkedDocument(linkedDocument);
  };

  const loadCertificationStatus = async () => {
    const status = await fetchAllDocumentPartStatus(rootId);
    setIsCertified(status.isCertifiedOrApproved);
    setIsCertifiedOrApprovedOrRejected?.(status.isCertifiedOrApprovedOrRejected);
  };

  const loadAllUserCheckout = async () => {
    const allUserCheckoutDocument = await fetchAllUserDocumentCheckout();
    store.dispatch(setAllUserDocumentCheckout(allUserCheckoutDocument));
  };

  const loadOutlineTree = async () => {
    try {
      const data = await fetchInitialDocumentPartTreeProps(selectedTreeItem, rootId, parents, setElementToUpdate);
      setTreeItems(data);
      return data;
    } catch (reason) {
      // The axios interceptor already surfaces an error notification; keep the previously
      // loaded tree instead of overwriting it with an empty one, and avoid an unhandled rejection.
      console.error("Failed to load the document outline structure", reason);
    } finally {
      setIsInitialLoading(false);
    }
  };

  async function loadData() {
    setIsInitialLoading(true);

    // These depend only on rootId/props (not on the tree result), so start them concurrently with
    // the slower tree fetch to overlap the network round-trips. They use notShowLoading (no global
    // spinner), and generatesElaboration on the tree detail call is a client-only flag ignored by
    // the backend, so there is no elaboration ordering to preserve.
    void loadInDesignLinkedDocument();
    void loadCertificationStatus();
    void loadAllUserCheckout();

    const loadedTreeItems = await loadOutlineTree();
    const currentDocumentName = loadedTreeItems?.[0]?.data?.name;
    if (currentDocumentName) {
      void checkSharepointName(currentDocumentName);
    }
  }

  function getDocumentIds() {
    const documentIds: string[] = [];
    for (const element of elementToUpdate) {
      documentIds.push(element.documentPart.id);
    }
    return documentIds;
  }

  function onClick(page: "dragedrop" | ToolsMenuPage) {
    if (page === "dragedrop") {
      updateTreeItems?.([], false, true);
      changePage("managestructure");
    } else if (page === "dbmapping") {
      changePage("dbmapping");
    } else if (page === "changetemplate") {
      changePage("changetemplate");
    } else {
      changePage("changecomponentsource");
    }
  }

  let notAuthorizedUser = isNotAuthorizedUser(UserFunctionEnum.MENU_OPEN_PANEL_MANAGE_TREE_STRUCTURE, userFunctions);
  let notAuthorizedUserForDbmapping = isNotAuthorizedUser(UserFunctionEnum.MENU_OPEN_PANEL_DBMAPPING, userFunctions);
  const [isCertified, setIsCertified] = useState(false);

  const isSendingIxbrl = useAppSelector(selectIsSendingIxbrl);

  function onRenameDocumentHandler(item: FlatPathTreeItem) {
    setTreeItems((prevData) =>
      treeMap(
        prevData,
        (t) => {
          return {
            ...t,
            renaming: t.key === item.key,
          };
        },
        {
          id: "key",
          children: "items",
        }
      )
    );
  }

  function handleDocumentNameChange(document: TreeViewDataItem, newName: string | undefined) {
    setTreeItems((prevData) =>
      treeMap(
        prevData,
        (t) => {
          return {
            ...t,
            renaming: false,
            text: newName !== undefined && newName !== "" && t.key === document.key ? newName : t.text,
          };
        },
        {
          id: "key",
          children: "items",
        }
      )
    );
    if (newName === undefined || newName === "") {
      return;
    }

    const selectedDocumentPart = (document.data.documentPart ?? document.data) as DocumentPart;
    startDocumentRenameElaboration(selectedDocumentPart, newName).then((r) =>
      console.debug("Rename elaboration request sent successfully", r)
    );
  }

  const startDocumentRenameElaboration = async (documentPartToRename: DocumentPart, newName: string) => {
    let elaboration: TgkRenameElaborationRequest = {
      correlationId: uuid.v4(),
      clientId: store.getState().configurationState.clientId,
      clientType: "USER",
      documentId: documentPartToRename.id,
      userId: username,
      channelIdToNotify: idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      tgkElaborationItemList: [],
    };
    const item: ElaborationItem = {
      elaborationType: "RENAME",
      correlationId: uuid.v4(),
      documentId: documentPartToRename.id,
      documentName: newName.replace(/\.docx$/i, ""),
      contentControlId: "",
    };
    elaboration.tgkElaborationItemList.push(item);
    await createElaborationRequestAsync(elaboration);
  };

  // Single source of truth for the Tools button label AND its menu chevron: both must always
  // look and behave identically (IDS-7053 styling fix).
  const toolsButtonColor = notAuthorizedUserForDbmapping ? "var(--color-grey)" : "var(--color-blue)";
  const toolsButtonCursor = notAuthorizedUserForDbmapping ? "default" : "pointer";
  // The chevron glyph is rendered by Fluent through the "ChevronDown" icon font (FabricMDL2Icons), not by
  // regular text, so `fontFamily` must never be overridden here or the arrow glyph would be replaced by
  // whatever character maps to that code point in a non-icon font. Only color and the font-size/font-weight
  // typography scale are safe to align with the label; every interactive/disabled slot is pinned to the same
  // color so Fluent's built-in theme gray (ActionButton's default `menuIcon` color) or hover/press/expanded
  // states never re-introduce a different color than the label.
  const toolsButtonStyles: IButtonStyles = {
    menuIcon: {
      color: toolsButtonColor,
      fontSize: "0.825rem",
      fontWeight: "bold",
      cursor: toolsButtonCursor,
    },
    menuIconHovered: { color: toolsButtonColor },
    menuIconPressed: { color: toolsButtonColor },
    menuIconExpanded: { color: toolsButtonColor },
    menuIconExpandedHovered: { color: toolsButtonColor },
    menuIconDisabled: { color: toolsButtonColor },
  };

  return (
    <>
      {isLibraryRibbon && (
        <div style={{ marginTop: "10px" }}>
          <p
            style={{
              color: "var(--color-grey)",
              marginBottom: "0px",
            }}
          >
            {formatMessage("cof.opendocuments.outline.description")}
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <ActionButton
              id={generateIdentifier("tools", "button")}
              text={formatMessage(CAPTION_BASE_TOOLS)}
              menuProps={getToolsMenuProps({
                hasReadPermission,
                notAuthorizedUserForDbmapping,
                isRootSubmitted: isDocumentPartSubmitted(rootId, documentPartWithStatus),
                onClick,
              })}
              iconProps={{
                imageProps: {
                  src: "/assets/Settings.png",
                  imageFit: ImageFit.contain,
                  width: "16px",
                  height: "16px",
                },
              }}
              styles={toolsButtonStyles}
              style={{
                fontSize: "0.825rem",
                fontWeight: "bold",
                color: toolsButtonColor,
                cursor: toolsButtonCursor,
              }}
              disabled={notAuthorizedUserForDbmapping}
            />
            <ActionButton
              text={formatMessage("cof.opendocuments.outline.managetreestructure")}
              onClick={() => onClick("dragedrop")}
              iconProps={iconManageStructure}
              style={{
                fontSize: "0.825rem",
                fontWeight: "bold",
                color:
                  notAuthorizedUser || isCertified || !hasReadPermission ? "var(--color-grey)" : "var(--color-blue)",
                cursor: notAuthorizedUser || isCertified || !hasReadPermission ? "default" : "pointer",
              }}
              disabled={notAuthorizedUser || isCertified || !hasReadPermission}
              title={isCertified ? formatMessage("cof.base.managetreestructuretitle") : null}
            />
          </div>
        </div>
      )}
      {isInitialLoading ? (
        <OutlineSkeleton />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
          <DocumentPartTree
            isOutlineRibbon={isOutlineRibbon}
            isSourcesRibbon={isSourcesRibbon}
            isLibraryRibbon={isLibraryRibbon}
            isOpenOutlineRibbon={isOpenOutlineRibbon}
            selectedPartId={parents?.length ? parents[parents?.length - 1]?.idFiglio : rootId}
            isLinkExisting={isLinkExisting}
            rootParts={treeItems}
            idPartRoot={idPartRoot}
            getIconButtonMenuProps={(item, itemHierarchicalIndex, documentPartWithStatus: DocumentPartWithStatus[]) => {
              if (isSourcesRibbon || !item.hasReadPermissions) return null;
              if (isOutlineRibbon)
                return getIconButtonOutlineRibbon(item, itemHierarchicalIndex, documentPartWithStatus);
              return getIconButtonMenuProps({
                itemSelected: item,
                itemHierarchicalIndex,
                userFunctions,
                defaultServiceProvider,
                seahorseDocumentLinked,
                inDesignLinkedDocument,
                allIxbrlSettings,
                idPartRoot,
                rootId,
                isSendingIxbrl,
                onRenameDocument: onRenameDocumentHandler,
                onDeleteDocument,
                onExportDocument,
                documentPartWithStatus,
                onCheckDifferencesDocument,
                onAlignDocument,
                isCertifiedOrApprovedOrRejected,
              });
            }}
            onItemClick={onItemClick}
            onDocumentNameChange={handleDocumentNameChange}
            isDocumentOutline={!isLinkExisting}
            updateTreeItems={updateTreeItems}
            openDocument={openDocumentById}
            parents={parents}
            setSelectedItem={onSetSelectedItems}
            elementToUpdate={setElementToUpdate}
            documentPartWithStatus={documentPartWithStatus}
            ignoreFetchPath={ignoreFetchPath}
            setDocumentPartWithStatus={setDocumentPartWithStatus}
            setTree={setTreeItems}
            siteId={siteId}
            driveItemId={driveItemId}
            enableFilter={enableFilter}
          />
        </div>
      )}
    </>
  );
};
